import { FC, useState } from 'react';
import { Link } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import { ActivityOverviewIcon, ExportIcon } from '../../../assets/icons/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import Button from '../../../components/button/index';
import { PublicCapTableError } from '../../../hooks/shared-cap-table/public-cap-table.api';
import { PublicCapTablePayload } from '../../../hooks/shared-cap-table/shared-cap-table.types';
import { StringKey } from '../../../lang/index';
import { PageRoute } from '../../../types/page.types';
import { DetailsSortVariant, detailsSortVariantTitleMap } from '../../../types/snapshot.types';
import { CompanyAvatar } from './company-avatar';
import { ConfidentialityBanner } from './confidentiality-banner';
import { PublicCapTableMobile } from './public-cap-table-mobile';
import { PublicCapTableOverviewCards } from './public-cap-table-overview';
import { PublicCapTableTable } from './public-cap-table-table';
import { PublicFooter } from './public-footer';
import { PublicOwnershipChart } from './public-ownership-chart';

type PublicCapTableViewProps = {
  exportError: null | PublicCapTableError;
  isExporting: boolean;
  onExport: () => void;
  payload: PublicCapTablePayload;
};

/**
 * The settings a viewer sees are the ones that were live when the page loaded, so an export can
 * legitimately be refused afterwards — the owner may have switched it off or revoked the link.
 * Those two say so; anything else is a transport failure worth retrying.
 */
const EXPORT_ERROR_MESSAGE: Record<PublicCapTableError, StringKey> = {
  [PublicCapTableError.EXPORT_DISABLED]: 'SHARE_PUBLIC_EXPORT_DISABLED',
  [PublicCapTableError.GONE]: 'SHARE_PUBLIC_EXPORT_GONE',
  [PublicCapTableError.PASSWORD_REQUIRED]: 'SHARE_PUBLIC_EXPORT_FAILED',
  [PublicCapTableError.THROTTLED]: 'SHARE_PUBLIC_EXPORT_FAILED',
  [PublicCapTableError.UNKNOWN]: 'SHARE_PUBLIC_EXPORT_FAILED',
};

export const PublicCapTableView: FC<PublicCapTableViewProps> = ({
  exportError,
  isExporting,
  onExport,
  payload,
}) => {
  const [selectedView, setSelectedView] = useState<DetailsSortVariant>(payload.defaultView);
  // The mobile design walks into positions on a screen of its own, so the blocks it does not carry
  // step aside while the viewer is in there.
  const [isMobileDrilled, setMobileDrilled] = useState(false);
  const drilledOnly = isMobileDrilled ? 'max-lg:hidden' : '';

  return (
    // `html, body, #root` are locked to 100svh with overflow hidden (index.css), so a page that
    // lives outside AppLayout must own its scroll container or it simply gets clipped.
    <div className="flex h-full flex-col overflow-y-auto bg-gray-50">
      <ConfidentialityBanner />

      {/* A phone gets the nav from its own design (393×76): a rounded, shadowed bar rather than the
          ruled one, with a 24px mark, a 16px title and a 44px button. */}
      <header className="flex items-center justify-between gap-3 bg-white px-4 py-4 max-lg:rounded-b-xl max-lg:shadow-xs lg:border-b lg:border-gray-100 lg:px-8 lg:py-3">
        <div className="flex min-w-0 items-center gap-2 lg:gap-3">
          <CompanyAvatar
            className="size-6 text-xs lg:size-8 lg:text-sm"
            companyName={payload.companyName}
            logoUrl={payload.companyLogoUrl}
          />
          <span className="truncate text-base font-semibold text-gray-700 lg:text-xl">
            <AppFormattedMessage
              id="SHARE_PUBLIC_CAP_TABLE_TITLE"
              values={{ company: payload.companyName ?? '' }}
            />
          </span>
        </div>

        <Link
          className="flex h-11 shrink-0 items-center justify-center whitespace-nowrap rounded bg-brand-700 px-4 text-sm font-[550] text-white lg:h-9"
          to={PageRoute.REGISTER}
        >
          <AppFormattedMessage id="SHARE_PUBLIC_SIGN_UP" />
        </Link>
      </header>

      {/* Inset to the width the authorised cap table gets: the app's collapsed sidebar takes 72px on
          the left (`px-4` + `w-10` + `px-4`) and the activity rail 96px on the right (`gap-4` +
          `w-14` + the layout's `lg:pr-6`). Without them the shared table rendered wider than the
          one it is a copy of. */}
      <main className="flex flex-1 flex-col gap-6 px-4 py-6 lg:pl-[72px] lg:pr-24">
        {(payload.overview || payload.ownership) && (
          <section
            className={`flex flex-col gap-4 rounded-xl bg-white p-4 shadow-sm ${drilledOnly}`}
          >
            <div className="flex items-center gap-2 rounded-md p-4 shadow-sm">
              <ActivityOverviewIcon />
              <span className="text-base font-semibold text-gray-700">
                <AppFormattedMessage id="SHARE_PUBLIC_OVERVIEW" />
              </span>
            </div>

            <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-2">
              {payload.overview && (
                <PublicCapTableOverviewCards
                  overview={payload.overview}
                  stakeholdersCount={payload.stakeholdersCount}
                />
              )}
              {payload.ownership && <PublicOwnershipChart ownership={payload.ownership} />}
            </div>
          </section>
        )}

        <section className="flex flex-col gap-4 rounded-xl bg-white p-4 shadow-sm">
          <span className={`text-base font-semibold text-gray-700 ${drilledOnly}`}>
            <AppFormattedMessage id="DETAILS" />
          </span>

          <div className={`flex items-center justify-between gap-3 ${drilledOnly}`}>
            {/* The view is chosen here on a wide screen and inside the positions step on a phone,
                where the design has no room for it beside the export control. */}
            <div className="flex h-9 divide-x divide-gray-200 rounded border border-gray-200 max-lg:hidden">
              {detailsSortVariantTitleMap.map(([viewKey, viewTitle]) => (
                <Button
                  className={twMerge(
                    'h-full w-fit rounded-none px-4 text-sm font-[450] text-[#858593] transition-colors duration-200',
                    selectedView === viewKey && 'bg-brand-25 font-[550] text-[#172335]',
                  )}
                  key={viewKey}
                  onClick={() => setSelectedView(viewKey)}
                  styleType="NONE"
                >
                  <AppFormattedMessage id={viewTitle} />
                </Button>
              ))}
            </div>

            {payload.allowExport && (
              <div className="flex items-center gap-3">
                {exportError && (
                  <span className="text-xs font-[450] text-fireside-600">
                    <AppFormattedMessage id={EXPORT_ERROR_MESSAGE[exportError]} />
                  </span>
                )}
                <Button
                  className="flex size-9 gap-1 border-[1px] border-gray-100 bg-gray-25 px-3 py-[6px] lg:w-fit"
                  disabled={isExporting}
                  isLoading={isExporting}
                  onClick={onExport}
                >
                  <ExportIcon className="rotate-180" />
                  <span className="text-sm font-[450] text-gray-700 max-lg:hidden">
                    <AppFormattedMessage id="EXPORT" />
                  </span>
                </Button>
              </div>
            )}
          </div>

          <div className="w-full overflow-x-auto max-lg:hidden">
            <PublicCapTableTable payload={payload} sortBy={selectedView} />
          </div>

          <div className="lg:hidden">
            <PublicCapTableMobile
              onDrilledChange={setMobileDrilled}
              onViewChange={setSelectedView}
              payload={payload}
              selectedView={selectedView}
            />
          </div>
        </section>
      </main>

      {/* Sits to the same edge as the table and the footer, not to the page's own. */}
      <div className="flex justify-end px-4 pb-2 text-xs font-[450] text-gray-400 lg:pl-[72px] lg:pr-24">
        <AppFormattedMessage
          id="SHARE_PUBLIC_UPDATED_AT"
          values={{ date: new Date(payload.updatedAt).toLocaleDateString('en-GB') }}
        />
      </div>

      <PublicFooter />
    </div>
  );
};
